import { Box, Typography, Button } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import React, { startTransition, Suspense, useMemo, useState } from 'react';
const MissedPaymentCard = React.lazy(() =>
  import('apps/admin/src/modules/student/MissedPaymentCard'),
);
import PropTypes from 'prop-types';

export default function GracePeriodBanner({
  checkGracePeriodPayment,
  className,
}) {
  const [restrictDashboard, setRestrictDashboard] = useState(false);
  const graceInfo = useMemo(() => {
    if (
      !Array.isArray(checkGracePeriodPayment) ||
      checkGracePeriodPayment.length === 0
    ) {
      return null;
    }

    // 1️⃣ Get earliest missed payment
    const earliestRecord = checkGracePeriodPayment.reduce((earliest, current) =>//NOSONAR
      new Date(current.missed_payment_date) <
      new Date(earliest.missed_payment_date)
        ? current
        : earliest,
    );

    const bufferDays = Number(earliestRecord.extented_grace_period || 0);

    // 2️⃣ Normalize missed date (DATE ONLY)
    const missed = new Date(earliestRecord.missed_payment_date);
    const missedDate = new Date(
      missed.getFullYear(),
      missed.getMonth(),
      missed.getDate(),
    );

    // 3️⃣ Calculate expiry date (DATE ONLY)
    const expiryDateOnly = new Date(missedDate);
    expiryDateOnly.setDate(expiryDateOnly.getDate() + bufferDays);

    // 4️⃣ Normalize today (DATE ONLY)
    const today = new Date();
    const todayDateOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    // 5️⃣ Calendar-day difference (NO time involved)
    const diffDays = Math.max(
      Math.round((expiryDateOnly - todayDateOnly) / (1000 * 60 * 60 * 24)),
      0,
    );

    // Optional: display expiry end-of-day for UI
    const expiryDateForUI = new Date(expiryDateOnly);
    expiryDateForUI.setHours(23, 59, 59, 999);

    return {
      missedDate,
      expiryDate: expiryDateForUI,
      bufferDays,
      daysRemaining: diffDays,
      isExpired: diffDays === 0,
    };
  }, [checkGracePeriodPayment]);

  if (!graceInfo) return null;

  const { expiryDate, daysRemaining } = graceInfo;
  return (
    <>
      <Box
        className={`payment-grace-period-banner-${className}`}
        sx={{
          backdropFilter: 'blur(8px)',
          backgroundColor:
            className === 'web'
              ? 'rgba(255, 82, 82, 0.08)'
              : 'rgba(252, 233, 233, 0.98)',
          border:
            className === 'mobile'
              ? '1px solid rgba(252, 251, 251, 0.96)'
              : '1px solid rgba(255, 82, 82, 0.25)',
          borderRadius: 1,
          px: 3,
          py: 1.5,
        }}
      >
        <Box display='flex' alignItems='center' gap={1.5}>
          <WarningAmberIcon color='error' />
          <Box
            className='scroll-wrapper'
            sx={{
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <Typography
              className='scroll-text'
              variant='body1'
              fontWeight={500}
              sx={{ p: 0 }}
            >
              <b>Payment issue detected.</b>
              Please update your payment method within{' '}
              <b>
                {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}
              </b>{' '}
              to avoid service interruption.
            </Typography>
          </Box>
        </Box>

        {/* Right content */}
        <Box
          className='expire-date-banner'
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          gap={2}
        >
          <Box display='flex' alignItems='center' gap={0.5}>
            <AccessTimeIcon fontSize='small' color='error' />
            <Typography variant='body2' sx={{ p: 0 }} color='text.secondary'>
              Ends <b>{expiryDate.toLocaleDateString()}</b>
            </Typography>
          </Box>

          <Button
            variant='contained'
            startIcon={<CreditCardIcon />}
            color='error'
            size='small'
            onClick={() => {
              startTransition(() => {
                setRestrictDashboard(true);
              });
            }}
          >
            Update Payment
          </Button>
        </Box>
      </Box>
      {restrictDashboard && (
        <Suspense fallback={null}>
          <MissedPaymentCard
            gracePeriodPayment={restrictDashboard}
            setRestrictDashboard={setRestrictDashboard}
          />
        </Suspense>
      )}
    </>
  );
}
GracePeriodBanner.propTypes = {
  checkGracePeriodPayment: PropTypes.array,
  className: PropTypes.oneOf(['web', 'mobile']),
};

GracePeriodBanner.defaultProps = {
  checkGracePeriodPayment: [],
  className: 'web',
};
